import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Loading } from '@bumble/app/components/Loading';
import { PageLoading } from '@bumble/app/components/PageLoading';
import { useApi } from '@bumble/app/hooks/use-api';
import DialogStandard from '../DialogStandard';
import {
  CELLULAR_PROD_CODES,
  CPE_MODE_PASSTHROUGH,
  CPE_MODE_PRIVATE_LINE,
  CPE_MODE_ROUTER,
} from '@bumble/app/constants';

interface RouterCreateProps {
  handleClose: () => void;
  // Receives the entity name the new CPE was created under (empty string
  // for account-level/no entity), so the caller can switch its own entity
  // filter to match and make the new CPE immediately visible.
  onSubmit?: (entityName?: string) => void;
}

export const RouterCreate = ({ handleClose, onSubmit }: RouterCreateProps) => {
  const api = useApi();
  const [loadingModalOpen, setLoadingModalOpen] = useState<boolean>(false);
  const [cpeName, setCpeName] = useState<string>('');
  const [productCode, setProductCode] = useState<string>('');
  const [selectedEntity, setSelectedEntity] = useState<string>('');
  const [siteStreet, setSiteStreet] = useState<string>('');
  const [siteCity, setSiteCity] = useState<string>('');
  const [siteStateProvince, setSiteStateProvince] = useState<string>('');
  const [sitePostalCode, setSitePostalCode] = useState<string>('');
  const [siteCountry, setSiteCountry] = useState<string>('');
  const [shipStreet, setShipStreet] = useState<string>('');
  const [shipCity, setShipCity] = useState<string>('');
  const [shipStateProvince, setShipStateProvince] = useState<string>('');
  const [shipPostalCode, setShipPostalCode] = useState<string>('');
  const [shipCountry, setShipCountry] = useState<string>('');
  const [wanCidr, setWanCidr] = useState<string>('');
  const [wanGateway, setWanGateway] = useState<string>('');
  const [wanDns, setWanDns] = useState<string>('');
  const [linkUpSpeed, setLinkUpSpeed] = useState<string>('');
  const [linkDownSpeed, setLinkDownSpeed] = useState<string>('');
  const [wanSnat, setWanSnat] = useState<boolean>(false);
  const [lanCidr, setLanCidr] = useState<string>('');
  const [providerServiceID, setProviderServiceID] = useState<string>('');
  const [clientCircuitID, setClientCircuitID] = useState<string>('');
  const [carrierInfo, setCarrierInfo] = useState<string>('');
  const [localSupportName, setLocalSupportName] = useState<string>('');
  const [localSupportEmail, setLocalSupportEmail] = useState<string>('');
  const [localSupportPhone, setLocalSupportPhone] = useState<string>('');
  const [shippingContactName, setShippingContactName] = useState<string>('');
  const [shippingContactEmail, setShippingContactEmail] = useState<string>('');
  const [shippingContactPhone, setShippingContactPhone] = useState<string>('');
  // Activated SIM ICCID, optional and only shown for cellular SKUs.
  const [activatedIccid, setActivatedIccid] = useState<string>('');
  const [dhcpClient, setDhcpClient] = useState<boolean>(false);
  const [pingDnsIp, setPingDnsIp] = useState<string>('');
  const [defaultGatewayIp, setDefaultGatewayIp] = useState<string>('');
  const [cpeMode, setCpeMode] = useState<string>(CPE_MODE_PASSTHROUGH);
  const routerMode = cpeMode === CPE_MODE_ROUTER;
  const privateLine = cpeMode === CPE_MODE_PRIVATE_LINE;
  const cellular = CELLULAR_PROD_CODES.includes(productCode);
  const [wan_sfp, setWanSFP] = useState<string>('sfp_mmf');
  const [lan_sfp, setLanSFP] = useState<string>('sfp_mmf');
  const [useWanSFP, setUseWanSFP] = useState<boolean>(false);
  const [useLanSFP, setUseLanSFP] = useState<boolean>(false);
  const [sanityCheckMessage, setSanityCheckMessage] = useState<string>('');
  const [sanityModalOpen, setSanityModalOpen] = useState<boolean>(false);
  const [lastPayload, setLastPayload] = useState<any | null>(null);
  /**
   * Define creating mode
   *
   * 0: default, no product code or 'BN-CPE-1000'
   * 1: 'BN-CPE-IP'
   * 3: 'BN-CPE-1000F', 'BN-CPE-1000FC', 'BN-CPE-10GF', 'BN-CPE-10GFC'
   */
  const [mode, setMode] = useState<number>(0);
  // 10G SKUs have SFP+ cages, front-panel labelled Eth0 (WAN) and Eth1 (LAN).
  const tenG = productCode === 'BN-CPE-10GF' || productCode === 'BN-CPE-10GFC';

  const appPost = useMutation(api.createCpeApiV1CreateCpePost, {
    onSuccess: (res, val) => {
      setLoadingModalOpen(false);
      if (res.status === 202) {
        setSanityCheckMessage((res.data as any).detail);
        setSanityModalOpen(true);
        setLastPayload(val);
        return;
      }
      toast.success('CPE router created.');
      onSubmit?.(selectedEntity);
      handleClose();
    },
    onError(error: any) {
      setLoadingModalOpen(false);
      if (Array.isArray((error as any).error.detail)) {
        (error as any).error.detail.forEach((el: any) =>
          toast.error(el.message, { autoClose: false, hideProgressBar: true }),
        );
      } else {
        toast.error((error as any).error.detail, { autoClose: false, hideProgressBar: true });
      }
    },
  });

  const handleSubmit = () => {
    // Build a structured address object, or undefined when every field is blank
    // (the API rejects an address with no fields, and site/shipping are optional).
    const makeAddress = (
      street: string,
      city: string,
      stateProvince: string,
      postalCode: string,
      country: string,
    ) => {
      const address = {
        street_address: street,
        city,
        state_province: stateProvince,
        postal_code: postalCode,
        country,
      };
      return Object.values(address).some((v) => v.trim()) ? address : undefined;
    };
    const siteAddress = makeAddress(siteStreet, siteCity, siteStateProvince, sitePostalCode, siteCountry);
    const shippingAddress = makeAddress(shipStreet, shipCity, shipStateProvince, shipPostalCode, shipCountry);

    let extra = {};
    switch (mode) {
      case 1:
        extra = {
          ping_dns_ip: pingDnsIp,
          ping_gw_ip: defaultGatewayIp,
        }
        break;
      case 3:
        if (isNaN(parseInt(linkUpSpeed))) {
          toast.error('Please enter a valid number for link up speed.');
          return;
        }
        if (isNaN(parseInt(linkDownSpeed))) {
          toast.error('Please enter a valid number for link down speed.');
          return;
        }
        extra = {
          ...(shippingAddress && { shipping_address: shippingAddress }),
          ...(useWanSFP && { wan_sfp }),
          ...(useLanSFP && { lan_sfp }),
          wan_tx_mbps: parseInt(linkUpSpeed),
          wan_rx_mbps: parseInt(linkDownSpeed),
          mode: cpeMode,
          ...(routerMode ? {
            ...(dhcpClient ? {
              wan_dhcp_c: true,
            } : {
              wan_dhcp_c: false,
              wan_cidr_addr: wanCidr,
              wan_default_gw_ip: wanGateway,
              wan_dns_ip: wanDns,
            })
          } : privateLine ? {} : {
            ...(dhcpClient ? {
              mgmt_dhcp_c: true,
            } : {
              mgmt_dhcp_c: false,
              mgmt_cidr_addr: wanCidr,
              mgmt_default_gw_ip: wanGateway,
              mgmt_dns_ip: wanDns,
            })
          }),
          ...(routerMode && {
            wan_snat: wanSnat,
            ...(lanCidr && { lan_cidr_addr: lanCidr }),
          })
        };
        break;
      default:
        if (isNaN(parseInt(linkUpSpeed))) {
          toast.error('Please enter a valid number for link up speed.');
          return;
        }
        if (isNaN(parseInt(linkDownSpeed))) {
          toast.error('Please enter a valid number for link down speed.');
          return;
        }
        extra = {
          ...(shippingAddress && { shipping_address: shippingAddress }),
          wan_tx_mbps: parseInt(linkUpSpeed),
          wan_rx_mbps: parseInt(linkDownSpeed),
          mode: cpeMode,
          ...(routerMode ? {
            ...(dhcpClient ? {
              wan_dhcp_c: true,
            } : {
              wan_dhcp_c: false,
              wan_cidr_addr: wanCidr,
              wan_default_gw_ip: wanGateway,
              wan_dns_ip: wanDns,
            })
          } : privateLine ? {} : {
            ...(dhcpClient ? {
              mgmt_dhcp_c: true,
            } : {
              mgmt_dhcp_c: false,
              mgmt_cidr_addr: wanCidr,
              mgmt_default_gw_ip: wanGateway,
              mgmt_dns_ip: wanDns,
            })
          }),
          ...(routerMode && {
            wan_snat: wanSnat,
            ...(lanCidr && { lan_cidr_addr: lanCidr }),
          })
        };
    }
    const payload = {
      cpe_name: cpeName,
      prod_code: productCode,
      ...(siteAddress && { site_address: siteAddress }),
      provider_service_id: providerServiceID,
      client_circuit_id: clientCircuitID,
      carrier_info: carrierInfo,
      local_support_contact_name: localSupportName,
      local_support_contact_email: localSupportEmail,
      local_support_contact_phone: localSupportPhone,
      contact_name: shippingContactName,
      contact_email: shippingContactEmail,
      contact_phone: shippingContactPhone,
      ...(cellular && activatedIccid.trim() && { activated_iccid: activatedIccid.trim() }),
      // Empty selection means account level (no entity), so omit entity_name.
      ...(selectedEntity && { entity_name: selectedEntity }),
      ...extra
    };
    setLastPayload(payload);
    setLoadingModalOpen(true);
    appPost.mutate(payload);
  };

  const isDisabled = () => {
    switch (mode) {
      case 1:
        return !(cpeName && productCode && pingDnsIp);
      default:
        // Require at least the street line of each address (site + shipping).
        return !(cpeName && productCode && shipStreet && siteStreet);
    }
  };

  const handleChangeMode = (event: React.ChangeEvent<HTMLInputElement>, value: string) => {
    setCpeMode(value);
  };

  const { isLoading, data: productCodeList } = useQuery(
    ['cpeProducts'],
    api.cpeProductsApiV1CpeProductsGet.bind(null, {}),
    { enabled: true },
  );

  const { data: entityNames } = useQuery(
    ['prepareEntityNames'],
    api.getEntityNamesApiV1GetEntityNamesGet.bind(null, {}),
    { enabled: true, cacheTime: 0, initialData: undefined },
  );

  useEffect(() => {
    switch (productCode) {
      case 'BN-CPE-IP':
        setMode(1);
        break;
      case 'BN-CPE-1000F':
      case 'BN-CPE-1000FC':
      case 'BN-CPE-10GF':
      case 'BN-CPE-10GFC':
        setMode(3);
        break;
      default:
        setMode(0);
        break;
    }
    if (!CELLULAR_PROD_CODES.includes(productCode)) {
      setCpeMode((current) => (current === CPE_MODE_PRIVATE_LINE ? CPE_MODE_PASSTHROUGH : current));
    }
  }, [productCode]);

  if (isLoading) return <Loading />;
  return (
    <Box pt={2}>
      <Grid container spacing={4}>
        <Grid item xs={12} sm={8}>
          <TextField
            fullWidth
            id="device_name"
            label="Device Name"
            variant="outlined"
            value={cpeName}
            onChange={(e) => setCpeName(e.target.value)}
            autoFocus
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth>
            <InputLabel id="product_code_label">Product Code</InputLabel>
            <Select
              labelId="product_code_label"
              id="product_code"
              label="Product Code"
              value={productCode}
              onChange={(e) => setProductCode(e.target.value)}
            >
              {productCodeList?.data.map((code: string) => (
                <MenuItem value={code} key={code}>
                  {code}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel id="entity_name_label">Entity</InputLabel>
            <Select
              labelId="entity_name_label"
              id="entity_name"
              label="Entity"
              value={selectedEntity}
              onChange={(e) => setSelectedEntity(e.target.value)}
            >
              <MenuItem value="">Account Level</MenuItem>
              {(entityNames?.data || []).map((name: string) => (
                <MenuItem value={name} key={name}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <Divider sx={{ mt: 4, mb: 8 }} />
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>Site Address</Typography>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="site_street_address"
                label="Street Address"
                variant="outlined"
                value={siteStreet}
                onChange={(e) => setSiteStreet(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City"
                variant="outlined"
                value={siteCity}
                onChange={(e) => setSiteCity(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="State / Province"
                variant="outlined"
                value={siteStateProvince}
                onChange={(e) => setSiteStateProvince(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Postal Code"
                variant="outlined"
                value={sitePostalCode}
                onChange={(e) => setSitePostalCode(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Country"
                variant="outlined"
                value={siteCountry}
                onChange={(e) => setSiteCountry(e.target.value)}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="provider_service_id"
            label="Provider Service ID"
            variant="outlined"
            value={providerServiceID}
            onChange={(e) => setProviderServiceID(e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="client_circuit_id"
            label="Client Circuit ID"
            variant="outlined"
            value={clientCircuitID}
            onChange={(e) => setClientCircuitID(e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="carrier_info"
            label="Carrier Info"
            variant="outlined"
            value={carrierInfo}
            onChange={(e) => setCarrierInfo(e.target.value)}
          />
        </Grid>
        {(mode === 0 || mode === 3) && <>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              id="linkdownloadspeed"
              label="Link Download Speed (mbps)"
              variant="outlined"
              value={linkDownSpeed}
              onChange={(e) => setLinkDownSpeed(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              id="linkuploadspeed"
              label="Link Upload Speed (mbps)"
              variant="outlined"
              value={linkUpSpeed}
              onChange={(e) => setLinkUpSpeed(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ mt: 4, mb: 8 }} />
          </Grid>
        </>}
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="local_support_contact_name"
            label="Local Support Contact Name"
            variant="outlined"
            value={localSupportName}
            onChange={(e) => setLocalSupportName(e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="local_support_contact_email"
            label="Local Support Contact Email"
            variant="outlined"
            value={localSupportEmail}
            onChange={(e) => setLocalSupportEmail(e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="local_support_contact_phone"
            label="Local Support Contact Phone"
            variant="outlined"
            value={localSupportPhone}
            onChange={(e) => setLocalSupportPhone(e.target.value)}
          />
        </Grid>
        {mode !== 1 && <> 
          <Grid item xs={12}>
            <Divider sx={{ mt: 4, mb: 8 }} />
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>Shipping Address</Typography>
            <Grid container spacing={4}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="shipping_street_address"
                  label="Street Address"
                  variant="outlined"
                  value={shipStreet}
                  onChange={(e) => setShipStreet(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="City"
                  variant="outlined"
                  value={shipCity}
                  onChange={(e) => setShipCity(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="State / Province"
                  variant="outlined"
                  value={shipStateProvince}
                  onChange={(e) => setShipStateProvince(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Postal Code"
                  variant="outlined"
                  value={shipPostalCode}
                  onChange={(e) => setShipPostalCode(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Country"
                  variant="outlined"
                  value={shipCountry}
                  onChange={(e) => setShipCountry(e.target.value)}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              id="shipping_name"
              label="Shipping Recipient Contact Name"
              variant="outlined"
              value={shippingContactName}
              onChange={(e) => setShippingContactName(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              id="shipping_email"
              label="Shipping Recipient Contact Email"
              variant="outlined"
              value={shippingContactEmail}
              onChange={(e) => setShippingContactEmail(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              id="shipping_phone"
              label="Shipping Recipient Contact Phone"
              variant="outlined"
              value={shippingContactPhone}
              onChange={(e) => setShippingContactPhone(e.target.value)}
              sx={{mb: 4}}
            />
          </Grid>
          {cellular && (
            <Grid item xs={12}>
              <Divider sx={{ mb: 8 }} />
              <TextField
                fullWidth
                id="activated_iccid"
                label="Activated ICCID (optional)"
                variant="outlined"
                value={activatedIccid}
                onChange={(e) => setActivatedIccid(e.target.value)}
              />
            </Grid>
          )}
        </>}
        {mode === 1 && <>
          <Grid item xs={12}>
            <TextField
              fullWidth
              id="ping_dns_ip"
              label="Monitoring Public IP (ipv4) or DNS name"
              variant="outlined"
              value={pingDnsIp}
              onChange={(e) => setPingDnsIp(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              id="default_gateway_ip"
              label="Default Gateway IP"
              variant="outlined"
              value={defaultGatewayIp}
              onChange={(e) => setDefaultGatewayIp(e.target.value)}
            />
          </Grid>
        </>}
      </Grid>
      {mode === 3 && <>
        <Divider sx={{ my: 4 }} />
        <Grid item xs={12} sm={6}>
          <Typography variant="body2" sx={{ mb: 4, display: 'inline-block', marginRight: '50px' }}>
            WAN Interface:
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={useWanSFP}
                onChange={(e) => setUseWanSFP(e.target.checked)}
              />
            }
            label={tenG ? 'Use SFP+ Port (Eth0)' : 'Use SFP Port (Eth4)'}
          />
          {useWanSFP && (
            <FormControl component="fieldset" sx={{ mt: 2, display:'flex', paddingLeft: '175px' }}>
              <RadioGroup
                row
                value={wan_sfp}
                onChange={(e) => setWanSFP(e.target.value)}
              >
                <FormControlLabel value="sfp_mmf" control={<Radio />} label="Multimode Fiber (MMF) (Short range)" />
                <FormControlLabel value="sfp_smf" control={<Radio />} label="Single-Mode Fiber (SMF) (Long range)" />
              </RadioGroup>
            </FormControl>)
          }
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="body2" sx={{ mb: 4, display: 'inline-block', marginRight: '55px' }}>
            LAN Interface:
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={useLanSFP}
                onChange={(e) => setUseLanSFP(e.target.checked)}
              />
            }
            label={tenG ? 'Use SFP+ Port (Eth1)' : 'Use SFP Port (Eth5)'}
          />
          {useLanSFP && (
            <FormControl component="fieldset" sx={{ mt: 2, display:'flex', paddingLeft: '175px' }}>
              <RadioGroup
                row
                value={lan_sfp}
                onChange={(e) => setLanSFP(e.target.value)}
              >
                <FormControlLabel
                  value="sfp_mmf"
                  control={<Radio />}
                  label="Multimode Fiber (MMF) (Short range)"
                />
                <FormControlLabel
                  value="sfp_smf"
                  control={<Radio />}
                  label="Single-Mode Fiber (SMF) (Long range)"
                />
              </RadioGroup>
            </FormControl>
          )}
        </Grid>
      </>}
      {mode !== 1 && <>
        <Divider sx={{ my: 4 }} />
        <FormControl fullWidth sx={{ mb: 4 }}>
          <RadioGroup
            row
            name="router-mode"
            onChange={handleChangeMode}
            value={cpeMode}
          >
            <FormControlLabel value={CPE_MODE_PASSTHROUGH} control={<Radio />} label="Pass through" />
            <FormControlLabel value={CPE_MODE_ROUTER} control={<Radio />} label="Router mode" />
            {cellular && (
              <FormControlLabel value={CPE_MODE_PRIVATE_LINE} control={<Radio />} label="Private Line" />
            )}
          </RadioGroup>
        </FormControl>
        {privateLine && (
          <Typography variant="body2" sx={{ mb: 4 }}>
            The circuit ({mode === 3 && useWanSFP && !tenG ? 'Eth4' : 'Eth0'}) and the LAN ports are bridged with no address; the CPE is
            managed over cellular only. The mode cannot be changed after creation.
          </Typography>
        )}
        {!privateLine && <>
        <Typography variant="body2" sx={{ mb: 4 }}>
          {routerMode ? 'WAN Interface (Eth0)' : 'Management Interface'}
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={dhcpClient}
                  onChange={(e) => setDhcpClient(e.target.checked)}
                />
              }
              label="DHCP Client"
            />
          </Grid>
          {!dhcpClient && <>
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="wan_cidr_ip_address"
                label="IP Address in CIDR format (Ex: A.B.C.D/24)"
                variant="outlined"
                value={wanCidr}
                onChange={(e) => setWanCidr(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="default_gateway_ip"
                label="Default Gateway IP"
                variant="outlined"
                value={wanGateway}
                onChange={(e) => setWanGateway(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="dns_server_ip"
                label="DNS Server IP(s)"
                variant="outlined"
                value={wanDns}
                onChange={(e) => setWanDns(e.target.value)}
              />
            </Grid>
          </>
          }
          {routerMode && <Grid item xs={12}>
            <FormControlLabel
              sx={{ mb: 4 }}
              control={
                <Checkbox
                  checked={wanSnat}
                  onChange={(e) => setWanSnat(e.target.checked)}
                />
              }
              label="Enable Source NAT"
            />
          </Grid>}
        </Grid>
        </>}
        {routerMode && <>
          <Divider sx={{ my: 4 }} />
          <Typography variant="body2" sx={{ mb: 8 }}>
            LAN Interface (Eth1)
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="lan_cidr_ip_address"
                label="IP Address in CIDR format (Ex: A.B.C.D/24)"
                variant="outlined"
                value={lanCidr}
                onChange={(e) => setLanCidr(e.target.value)}
              />
            </Grid>
          </Grid>
        </>}
      </>}
      <Box display="flex" justifyContent="space-between" sx={{ mt: 10 }}>
        <Button variant="outlined" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          type="submit"
          onClick={handleSubmit}
          disabled={isDisabled()}
        >
          Create
        </Button>
      </Box>
      {loadingModalOpen && <PageLoading content="Working on it..." />}
      {sanityModalOpen && (
        <DialogStandard
          open={sanityModalOpen}
          handleClose={() => setSanityModalOpen(false)}
          title="Sanity Check"
          size="sm"
        >
          <Box sx={{ mt: 1 }}>
            <Typography sx={{ mb: 3 }}>{sanityCheckMessage}</Typography>
            <Box display="flex" justifyContent="flex-end" gap={2} mt={4}>
              <Button
                variant="outlined"
                onClick={() => {
                  setSanityModalOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  setSanityModalOpen(false);

                  if (!lastPayload) return;

                  const payloadWithSkip = {
                    ...lastPayload,
                    skip_default_gw_ping: true,
                  };

                  setLoadingModalOpen(true);
                  appPost.mutate(payloadWithSkip);
                }}
              >
                Confirm
              </Button>
            </Box>
          </Box>
        </DialogStandard>
      )}
    </Box>
  );
};
